import {
  AdConstraint,
  AdGroupConstraint,
  AdsCampaignConstraint,
  CityConstraint,
  Constraints,
  ConstraintType,
  CountryConstraint,
  DeviceConstraint,
  KeywordConstraint,
  RegionConstraint,
  ScheduleConstraint,
} from '@superfunnel/types';

export const getSchedulesString = (schedule: ScheduleConstraint) => {
  const parts: string[] = [];

  // Add days of week if available
  if (schedule.daysOfWeek?.length) {
    parts.push(`Days: ${schedule.daysOfWeek.join(', ')}`);
  }

  // Add hours of day if available
  if (schedule.hoursOfDay?.length) {
    const hours = schedule.hoursOfDay.sort((a, b) => a - b);
    const startHour = hours[0];
    const endHour = hours[hours.length - 1];
    if (startHour === endHour) {
      parts.push(`Time: ${startHour}:00`);
    } else {
      parts.push(`Time: ${startHour}:00 - ${endHour + 1}:00`);
    }
  }

  // Add date range if available
  if (schedule.startDate || schedule.endDate) {
    const dateRange = [schedule.startDate, schedule.endDate]
      .filter(Boolean)
      .join(' to ');
    if (dateRange) {
      parts.push(`Dates: ${dateRange}`);
    }
  }

  // Add timezone if available
  if (schedule.timeZone) {
    parts.push(`Timezone: ${schedule.timeZone}`);
  }

  return parts.length > 0 ? parts.join(' • ') : 'Scheduled';
};

export function getConstraintTitle<T extends ConstraintType>(
  type: T,
  constraint: NonNullable<Constraints[T]>[0],
): string {
  switch (type) {
    case 'schedules':
      return getSchedulesString(constraint as ScheduleConstraint);
    case 'devices':
      return constraint as DeviceConstraint;
    case 'campaigns':
      return (constraint as AdsCampaignConstraint).name || 'Campaign';
    case 'adGroups':
      return (constraint as AdGroupConstraint).name || 'Ad Group';
    case 'ads': {
      const ad = constraint as AdConstraint;
      if (ad.provider === 'google') {
        return ad.headlines?.[0]?.text || 'Google Ad';
      }
      if (ad.provider === 'facebook') {
        return ad.titles?.[0]?.text || 'Facebook Ad';
      }
      return `Ad: ${(ad as AdConstraint).id}`;
    }
    case 'keywords':
      return (constraint as KeywordConstraint).keyword || 'Keyword';
    case 'countries':
      return (constraint as CountryConstraint).name || 'Country';
    case 'regions':
      return (constraint as RegionConstraint).name || 'Region';
    case 'cities':
      return (constraint as CityConstraint).displayName || 'City';
    default:
      return 'Unknown Constraint';
  }
}
